import { useMemo, useState } from 'react'

import { errorView, resourceTag, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  NEW_PRICE_TYPE_DRAFT,
  ONEC_PRICING_TAG,
  WRITE_DISABLED_REASON,
  PriceTypesScreen,
  makeSelectPriceTypeRows,
  onecConnectionForm,
  onecCreatePriceType,
  onecMarkPriceType,
  onecPriceTypes,
  onecUpdatePriceType,
  priceTypeDraftProblem,
  pricingApi,
  type PriceTypeDraft,
  type PriceTypesActions,
  type PriceTypesVm,
} from '@krp/resource-onec-ui'

import { useAppDispatch } from '../../app/hooks'
import { useResourceRevision } from '../platform/useResourceRevision'

/**
 * Binding вкладки «Цены» базы 1С (RP-FB39): виды цен базы. Создать, изменить, пометить на удаление — только с правом
 * `prices.write` этой базы; «Цены» у вида открывают цены товаров этого вида (адрес с ключом вида). Черновик формы и
 * ожидание подтверждения живут, пока открыт экран; справочник — в кэше запроса, не в слайсе.
 */
export function PriceTypesBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const args = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const list = useQueryResult(onecPriceTypes, args)
  const selectRows = useMemo(() => makeSelectPriceTypeRows(), [])
  const { query, rows } = selectRows(list)
  const canWrite = useResourceRevision(resourceId, onecConnectionForm.kind, 'prices.write') !== null

  const [draft, setDraft] = useState<PriceTypeDraft | null>(null)
  const [confirmMarkKey, setConfirmMarkKey] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [create, createState] = onecCreatePriceType.useMutation({ fixedCacheKey: `${resourceId}:price-type-create` })
  const [update, updateState] = onecUpdatePriceType.useMutation({ fixedCacheKey: `${resourceId}:price-type-update` })
  const [mark, markState] = onecMarkPriceType.useMutation({ fixedCacheKey: `${resourceId}:price-type-mark` })
  const saveState = draft?.key == null ? createState : updateState

  const vm = useMemo<PriceTypesVm>(
    () => ({
      query,
      rows,
      form:
        draft === null
          ? null
          : {
              open: true,
              draft,
              problem: priceTypeDraftProblem(draft),
              saving: saveState.isLoading,
              error: saveState.isError && saveState.error !== undefined ? toErrorVm(saveState.error) : null,
            },
      confirmMarkKey,
      marking: markState.isLoading,
      markError: markState.isError && markState.error !== undefined ? toErrorVm(markState.error) : null,
      done,
      canWrite,
      writeDisabledReason: canWrite ? null : WRITE_DISABLED_REASON,
    }),
    [canWrite, confirmMarkKey, done, draft, markState, query, rows, saveState],
  )

  const actions = useMemo<PriceTypesActions>(
    () => ({
      reload: () => dispatch(pricingApi.util.invalidateTags([resourceTag(ONEC_PRICING_TAG, resourceId, 'PRICE-TYPES')])),
      create: () => {
        createState.reset()
        setDone(null)
        setDraft(NEW_PRICE_TYPE_DRAFT)
      },
      edit: (key) => {
        const type = list.data?.data.items.find((item) => item.key === key)
        if (type === undefined) return
        updateState.reset()
        setDone(null)
        setDraft({ key, name: type.name, includesVat: type.includesVat, comment: type.comment ?? '', inactive: type.inactive })
      },
      draftChanged: (next) => setDraft(next),
      closeForm: () => setDraft(null),
      save: () => {
        if (draft === null || priceTypeDraftProblem(draft) !== null) return
        const name = draft.name.trim()
        const comment = draft.comment.trim() === '' ? null : draft.comment.trim()
        const request =
          draft.key === null
            ? create({ resourceId, name, includesVat: draft.includesVat, comment })
            : update({ resourceId, typeKey: draft.key, name, includesVat: draft.includesVat, comment, inactive: draft.inactive })
        void request
          .unwrap()
          .then((result) => {
            setDone(draft.key === null ? `Вид цен «${result.data.name}» создан в 1С` : `Вид цен «${result.data.name}» сохранён`)
            setDraft(null)
          })
          .catch(() => undefined)
      },
      askMark: (key) => {
        markState.reset()
        setDone(null)
        setConfirmMarkKey(key)
      },
      cancelMark: () => setConfirmMarkKey(null),
      confirmMark: () => {
        const type = list.data?.data.items.find((item) => item.key === confirmMarkKey)
        if (type === undefined) return
        void mark({ resourceId, typeKey: type.key, deletionMark: !type.deletionMark })
          .unwrap()
          .then((result) => {
            setDone(result.data.deletionMark ? `Вид цен «${result.data.name}» помечен на удаление` : `Пометка удаления с «${result.data.name}» снята`)
            setConfirmMarkKey(null)
          })
          .catch(() => setConfirmMarkKey(null))
      },
    }),
    [confirmMarkKey, create, createState, dispatch, draft, list.data, mark, markState, resourceId, update, updateState],
  )

  return <PriceTypesScreen vm={vm} actions={actions} />
}

function toErrorVm(error: Parameters<typeof errorView>[0]) {
  const view = errorView(error)
  return { message: view.message, code: view.code, correlationId: view.correlationId }
}
